import {SoundAreaName, Zone} from "../../../components/types";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    ExpandedIndex,
    Grid,
    Stack,
    Text
} from "@chakra-ui/react";
import * as React from "react";
import {useCallback, useContext, useMemo, useState, useEffect} from "react";
import AreaZoneSoundFile from "./AreaZoneSoundFile";
import ZoneTriggers from "./ZoneTriggers";
import {MapContext, MapContextI} from "../../../components/context/MapContextWrapper";

export type SoundFileDef = {
    title: string
    name: SoundAreaName
    showFadeControls?: boolean
}

type Props = {
    zone: Zone
    description: string
    soundFiles: SoundFileDef[]
    children?: any
    allowGpsTriggers?: boolean
    rulesContent?: React.ReactNode
    headerContent?: React.ReactNode
    soundFilesDisabled?: boolean
    initialExpandedIndex?: ExpandedIndex
    onExpandedIndexChange?: (index: ExpandedIndex) => void
}

/**
 * A zone is basically a sound spot which is predefined and limited in functionality
 * A zone is defined as a circle or as a polygon
 */
const AreaZone = ({zone, description, soundFiles, children, allowGpsTriggers, rulesContent, headerContent, soundFilesDisabled, initialExpandedIndex, onExpandedIndexChange}: Props) => {
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const hasGeneral = !!children;
    const hasSoundFiles = (soundFiles && soundFiles.length > 0);
    const hasRules = !!rulesContent;
    const sections = useMemo(() => {
        const list: string[] = [];
        if (hasGeneral) {
            list.push("general");
        }
        if (hasSoundFiles) {
            list.push("soundFiles");
        }
        list.push("triggers");
        if (hasRules) {
            list.push("rules");
        }
        return list;
    }, [hasGeneral, hasSoundFiles, hasRules]);
    const soundFilesIndex = useMemo(() => {
        const idx = sections.indexOf("soundFiles");
        return idx >= 0 ? idx : undefined;
    }, [sections]);
    const defaultExpanded = useMemo<ExpandedIndex>(() => (sections.length > 0 ? [0] : []), [sections.length]);
    const [expandedIndex, setExpandedIndexInternal] = useState<ExpandedIndex>(initialExpandedIndex ?? defaultExpanded);
    const setExpandedIndex = useCallback((next: React.SetStateAction<ExpandedIndex>) => {
        setExpandedIndexInternal((prev) => {
            const resolved = typeof next === "function" ? (next as (p: ExpandedIndex) => ExpandedIndex)(prev) : next;
            if (onExpandedIndexChange) {
                onExpandedIndexChange(resolved);
            }
            return resolved;
        });
    }, [onExpandedIndexChange]);

    useEffect(() => {
        if (!Array.isArray(expandedIndex)) {
            return;
        }
        const filtered = expandedIndex.filter((idx) => idx >= 0 && idx < sections.length);
        if (filtered.length !== expandedIndex.length) {
            setExpandedIndex(filtered);
        }
    }, [expandedIndex, sections.length]);

    useEffect(() => {
        if (!soundFilesDisabled || soundFilesIndex === undefined) {
            return;
        }
        if (!Array.isArray(expandedIndex)) {
            return;
        }
        if (expandedIndex.includes(soundFilesIndex)) {
            setExpandedIndex(expandedIndex.filter((idx) => idx !== soundFilesIndex));
        }
    }, [expandedIndex, soundFilesDisabled, soundFilesIndex]);

    // Auto-expand "Triggers" section when a GPS trigger on this zone is selected from the map
    const triggerSelection = mapContext.getTriggerSelection();
    useEffect(() => {
        if (!triggerSelection) return;
        const gpsTriggers = zone?.gpsTriggers;
        if (!gpsTriggers || !gpsTriggers.some(t => t.id === triggerSelection)) return;
        const triggersIndex = sections.indexOf("triggers");
        if (triggersIndex < 0) return;
        setExpandedIndex(prev => {
            const arr = Array.isArray(prev) ? [...prev] : [];
            if (arr.includes(triggersIndex)) return prev;
            return [...arr, triggersIndex];
        });
    }, [triggerSelection, zone?.gpsTriggers, sections]);

    const handleAccordionChange = (nextExpanded: ExpandedIndex) => {
        setExpandedIndex(nextExpanded);
        const indices = Array.isArray(nextExpanded) ? nextExpanded : [nextExpanded];
        const triggersIndex = sections.indexOf("triggers");
        if (triggersIndex > -1 && indices.includes(triggersIndex)) {
            const gpsTriggers = zone?.gpsTriggers;
            if (gpsTriggers && gpsTriggers.length > 0) {
                const triggerId = gpsTriggers[0].id;
                mapContext.setTriggerSelection(triggerId);
                return;
            }
        }
        mapContext.setTriggerSelection(undefined);
    };

    if (zone) {
        return <Box borderWidth={"2px"} padding={"4px"}>
            <Grid>
                {headerContent ? (
                    <Box marginBottom={"8px"}>
                        {headerContent}
                    </Box>
                ) : null}
                {description && description.length>0 ? <Text>{description}</Text> : null}
                <Accordion
                    allowToggle
                    allowMultiple
                    index={expandedIndex}
                    onChange={handleAccordionChange}
                    size={"sm"}
                    marginTop={"16px"}
                >
                    {
                        children ?
                            <AccordionItem>
                                <AccordionButton>
                                    <Text fontWeight={"medium"}>General</Text>
                                    <AccordionIcon/>
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    {children}
                                </AccordionPanel>
                            </AccordionItem>
                            : null
                    }

                    {
                        soundFiles && soundFiles.length > 0 ?
                            <AccordionItem isDisabled={soundFilesDisabled}>
                                <AccordionButton>
                                    <Text fontWeight={"medium"} color={soundFilesDisabled ? "gray.400" : undefined}>
                                        Sound files
                                    </Text>
                                    <AccordionIcon color={soundFilesDisabled ? "gray.400" : undefined}/>
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <Box opacity={soundFilesDisabled ? 0.5 : 1} pointerEvents={soundFilesDisabled ? "none" : "auto"}>
                                        <Stack>
                                            {
                                                soundFiles.map((def: SoundFileDef, indx: number) =>
                                                <AreaZoneSoundFile key={"areaZone-" + indx}
                                                                   title={def.title}
                                                                   soundName={def.name}
                                                                   zone={zone}
                                                                   showFadeControls={def.showFadeControls}/>
                                                )
                                            }
                                        </Stack>
                                    </Box>
                                </AccordionPanel>
                            </AccordionItem>
                            : null
                    }
                    <AccordionItem>
                        <AccordionButton>
                            <Text fontWeight={"medium"}>Triggers</Text>
                            <AccordionIcon/>
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                            <ZoneTriggers zone={zone} key={"zone_" + zone.id} allowGps={allowGpsTriggers}/>
                        </AccordionPanel>
                    </AccordionItem>
                    {rulesContent ? (
                        <AccordionItem>
                            <AccordionButton>
                                <Text fontWeight={"medium"}>Rules</Text>
                                <AccordionIcon/>
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                                {rulesContent}
                            </AccordionPanel>
                        </AccordionItem>
                    ) : null}

                </Accordion>


            </Grid>
        </Box>
    } else {
        return <Box>No spot configured</Box>
    }
}

export default AreaZone;
